import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import type { UrlPatternField as UrlPatternFieldType } from "../../../shared/connectors/types";
import {
  domainToRegex,
  regexToDomain,
  urlPatternError,
} from "../../../shared/connectors/urlPatternConversion";
import { sharedMutators } from "../../../shared/mutators";
import {
  queueConnectorEdit,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { Box } from "../Box";
import { Button } from "../Button";
import { Input } from "../Input";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import type { App } from "./Form";
import { useConfirmGrantRemoval } from "./useConfirmGrantRemoval";

const HelperRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 8px;
`;

const ModeSwitcher = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModeTab = styled(PressButton)`
  all: unset;
  cursor: pointer;
  font-weight: 350;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0.3;
  &[data-active] {
    opacity: 1;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const Dot = styled("span")`
  font-size: 10px;
  line-height: 12px;
  color: var(--text-primary);
  opacity: 0.3;
`;

const DiscardButton = styled(PressButton)`
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
`;

type Props = {
  item: UrlPatternFieldType;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
  saveError: boolean;
  onSaveStatus: (saved: boolean) => void;
};

export function UrlPatternField({
  item,
  app,
  disabled,
  onEdit,
  saveError,
  onSaveStatus,
}: Props) {
  const z = useZero();
  const { confirmGrantRemoval, confirmModal } = useConfirmGrantRemoval(app);
  const appRef = useRef(app);
  const failedAttempt = useRef<{ baseApp: App; urlPattern: string } | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    appRef.current = app;
  });
  const currentRegex = app.urlPattern;
  const isMatchAll = !currentRegex || currentRegex === ".*";
  const parsedDomain = isMatchAll ? "" : regexToDomain(currentRegex);

  const [mode, setMode] = useState<"domain" | "regex">(
    parsedDomain === null ? "regex" : "domain"
  );
  const [error, setError] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [retryable, setRetryable] = useState(false);

  const isDomain = mode === "domain";
  const storedValue = isDomain ? (parsedDomain ?? "") : (currentRegex ?? "");

  const validate = (input: HTMLInputElement) => {
    const regex = isDomain ? domainToRegex(input.value) : input.value;
    const problem = urlPatternError(regex);
    input.setCustomValidity(problem ?? "");
    setError(problem);
    return problem;
  };

  const persist = (input: HTMLInputElement, explicitRetry = false) => {
    if (!explicitRetry && (retryable || (saveError && draftValue === null)))
      return;
    const regex = isDomain ? domainToRegex(input.value) : input.value;
    if (validate(input)) return;
    if (regex === appRef.current.urlPattern && !retryable && !saveError) {
      onSaveStatus(true);
      setError(null);
      setDraftValue(null);
      setRetryable(false);
      return;
    }
    confirmGrantRemoval(
      { urlPattern: regex },
      () => {
        input.setCustomValidity("Please wait for the URL change to save.");
        setSaving(true);
        void queueConnectorEdit(app.tenantId, app.id, async () => {
          const currentApp = appRef.current;
          const baseApp =
            failedAttempt.current?.urlPattern === currentApp.urlPattern
              ? failedAttempt.current.baseApp
              : currentApp;
          try {
            const update = await withConnectorDestinationProof(baseApp, {
              urlPattern: regex,
            });
            const result = await z.mutate(
              sharedMutators.updateConnectedApp(update)
            ).server;
            if (result.type !== "success") throw new Error();
            failedAttempt.current = null;
            input.setCustomValidity("");
            onSaveStatus(true);
            setError(null);
            setDraftValue(null);
            setRetryable(false);
          } catch {
            failedAttempt.current = { baseApp, urlPattern: regex };
            const message = "The URL change wasn’t saved. Try again.";
            input.setCustomValidity(message);
            onSaveStatus(false);
            setError(message);
            setRetryable(true);
          } finally {
            setSaving(false);
          }
        });
      },
      () => {
        if (retryable || saveError) return;
        input.value = storedValue;
        input.setCustomValidity("");
        onSaveStatus(true);
        setDraftValue(null);
        setError(null);
        setRetryable(false);
      }
    );
  };
  const canSimplify = parsedDomain !== null;

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {isDomain ? "Allowed for URLs starting with" : "Allowed URL patterns"}
        </Text>
      </Box>
      <Input
        key={mode}
        ref={inputRef}
        type="text"
        value={draftValue ?? storedValue}
        placeholder={isDomain ? item.placeholder : ".*"}
        onChange={(e) => {
          onEdit?.();
          setDraftValue(e.target.value);
          setRetryable(false);
          validate(e.target);
        }}
        onBlur={(e) => persist(e.target)}
        style={{ width: "100%" }}
        readOnly={disabled || saving}
      />
      <HelperRow>
        <Text
          variant="text-small"
          color={
            error || saveError ? "var(--red-600)" : "var(--text-secondary)"
          }
        >
          {error ??
            (saveError
              ? "The URL change wasn’t saved. Try again."
              : app.secretKeys?.length
                ? "If asked, re-enter the credential to change where this connector sends it. You can then edit that destination for one minute."
                : isDomain
                  ? "Use * as a wildcard, e.g. *.example.com."
                  : "URLs matching this pattern will use your credentials.")}
        </Text>
        {retryable && (
          <Button
            size="m"
            variant="secondary"
            disabled={saving}
            onClick={() => {
              if (inputRef.current) persist(inputRef.current, true);
            }}
          >
            Retry change
          </Button>
        )}
        {saveError && !retryable && draftValue === null && (
          <DiscardButton
            onPress={() => {
              inputRef.current?.setCustomValidity("");
              onSaveStatus(true);
              setError(null);
            }}
          >
            Discard change
          </DiscardButton>
        )}
        {canSimplify && !retryable && !saveError && (
          <ModeSwitcher>
            <ModeTab
              data-active={isDomain || undefined}
              onPress={() => {
                if (!isDomain) {
                  setMode("domain");
                  setError(null);
                  setDraftValue(null);
                  inputRef.current?.setCustomValidity("");
                }
              }}
            >
              Simple
            </ModeTab>
            <Dot>•</Dot>
            <ModeTab
              data-active={!isDomain || undefined}
              onPress={() => {
                if (isDomain) {
                  setMode("regex");
                  setError(null);
                  setDraftValue(null);
                  inputRef.current?.setCustomValidity("");
                }
              }}
            >
              Regex
            </ModeTab>
          </ModeSwitcher>
        )}
      </HelperRow>
      {confirmModal}
    </div>
  );
}
